import React from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import useTheme from "../../common/hooks/useTheme";
import { Box } from "@mui/material";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material/styles";

interface CustomButtonProps extends ButtonProps {
  label: string;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  sx?: SxProps<Theme>;
  isDisable?: boolean;
  // Keep icon and label together as one centered group. By default the label
  // fills the free width (flex: 1), which on full-width buttons parks the
  // icon at the far-left edge away from the centered text.
  centerContent?: boolean;
}

const ActionButton: React.FC<CustomButtonProps> = ({
  onClick,
  label,
  sx,
  icon,
  iconRight,
  isDisable = false,
  centerContent = false,
}) => {
  const theme = useTheme();

  return (
    <Button
      onClick={onClick}
      sx={{
        height: "38px",
        border: `1px solid ${
          isDisable
            ? theme.colors_ds.NEUTRAL_BORDER_WEAK
            : theme.colors_ds.NEUTRAL_BORDER_WEAK
        }`,
        textTransform: "none",
        borderRadius: "8px",
        fontFamily: "var(--font-Medium)",
        fontSize: "14px",
        padding: "8px 16px",
        backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
        color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        "&:hover": {
          backgroundColor: theme.colorTokens.P[10],
        },
        "&:active": {
          backgroundColor: theme.colorTokens.P[20],
        },
        "& .MuiTouchRipple-root": {
          color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
        },
        // MUI greys only the label when disabled; icons passed in carry
        // their own fills, so fade them too and keep the label a weak grey.
        "&.Mui-disabled": {
          color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
          "& svg": { opacity: 0.4 },
        },
        ...sx, // Allow additional styles to be passed via props
      }}
      disabled={isDisable}
    >
      {icon && (
        <Box
          sx={{
            marginRight: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      )}
      <Box style={centerContent ? undefined : { flex: 1 }}>{label}</Box>
      {iconRight && (
        <Box
          sx={{
            marginLeft: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {iconRight}
        </Box>
      )}
    </Button>
  );
};

export default ActionButton;
